import React from 'react';
import * as d3 from 'd3';
import cytoscape from 'cytoscape';
import contextMenus from 'cytoscape-context-menus';
import 'cytoscape-context-menus/cytoscape-context-menus.css';
import { mergeStyleSets } from '@fluentui/react';

import ChartDefaults from '../../../shared/ChartDefaults';
import Store from '../../../shared/state/Store';
import CytoReducer from './CytoReducer';
import ModellerReducer from '../../ModellerReducer';
import ModellerDefaults from '../../ModellerDefaults';
import GraphStyles from './GraphStyles';
import GraphUtils from '../../GraphUtils';

cytoscape.use(contextMenus);

const MODEL_VIEW_KEY = ModellerDefaults.ModelViewGraphKey;

const componentStyles = mergeStyleSets({
  container: {
    width: '100%',
    height: ModellerDefaults.ComponentDefaults.ComponentHeight,
    border: '1px solid #e4e4e4',
  },
});

const color = () => {
  const scale = d3.scaleOrdinal(d3.schemeCategory10);
  return (d) => scale(d.group);
};

export const getColor = (group) => (ChartDefaults.Colors[group] ? ChartDefaults.Colors[group] : color);

const stableStringify = (obj) => {
  try { return JSON.stringify(obj); } catch (e) { return ''; }
};

const normalizeModelViewElementsForCompare = (elements) => {
  if (!elements || !elements.nodes) return elements;

  const nodes = elements.nodes.map((n) => {
    // network parent positions will change after we apply 3B (and after user drags),
    // so ignore them for "do we need to recreate cy?"
    if (n?.classes?.includes?.('network-node')) {
      const { position, ...rest } = n;
      return rest;
    }
    return n;
  });

  const edges = elements.edges ? elements.edges.slice() : [];
  return { nodes, edges };
};

/**
 * Extract topology-only signature (node/edge ids) for rebuild detection.
 * Ignores positions, styles, and other visual properties.
 */
const getTopologyKey = (elements) => {
  if (!elements) return '';
  const nodeIds = (elements.nodes || []).map((n) => n?.data?.id || '').sort().join(',');
  const edgeIds = (elements.edges || []).map((e) => e?.data?.id || '').sort().join(',');
  return stableStringify({ nodeIds, edgeIds });
};

/**
 * Apply persisted network positions from model graphics to elements.
 * Patches network-node positions when valid saved positions exist.
 */
const applyPersistedNetworkPositionsToElements = (elements, model) => {
  if (!elements || !elements.nodes || !model || !model.networks) return elements;

  const networksById = {};
  model.networks.forEach((net) => {
    if (net.id) networksById[net.id] = net;
  });

  let hasStackedPositions = false;
  let stackedCount = 0;

  const patchedNodes = elements.nodes.map((node) => {
    const classes = node?.classes;
    const isNetworkNode = Array.isArray(classes) ? classes.includes('network-node') : (typeof classes === 'string' && classes.includes('network-node'));

    if (!isNetworkNode) return node;

    const networkId = node?.data?.networkId;
    if (!networkId) return node;

    const network = networksById[networkId];
    const savedPos = network?.graphics?.graph?.position;

    // Check if incoming position is near origin
    const incomingPos = node?.position;
    if (incomingPos && typeof incomingPos.x === 'number' && typeof incomingPos.y === 'number') {
      const nearOrigin = Math.abs(incomingPos.x) < 10 && Math.abs(incomingPos.y) < 10;
      if (nearOrigin) {
        hasStackedPositions = true;
        stackedCount += 1;
      }
    }

    // Apply saved position if valid (numbers and not default {0,0})
    if (savedPos && typeof savedPos.x === 'number' && typeof savedPos.y === 'number') {
      const isDefault = savedPos.x === 0 && savedPos.y === 0;
      if (!isDefault) {
        return { ...node, position: { x: savedPos.x, y: savedPos.y } };
      }
    }

    return node;
  });

  return { ...elements, nodes: patchedNodes };
};

/**
 * 3B: Compute network positions by laying out a headless synthetic graph.
 *
 * IMPORTANT: cytoscape headless layouts typically produce *very small* coordinates
 * (like x=0.5, y=3.0). Those are not pixel units. We must scale/normalize them
 * to sensible pixel spacing before translating real compounds, otherwise everything
 * “collapses” visually.
 */
const computeNetworkPositionsBreadthfirst = (model, opts = {}) => {
  const spacingFactor = opts.spacingFactor ?? 1.4;
  const padding = opts.padding ?? 50;

  const networks = model?.networks ?? [];
  const links = model?.links ?? [];

  const synNodes = networks.map((net) => ({
    data: { id: `syn_${net.id}`, networkId: net.id },
  }));

  const edgeSet = new Set();
  const synEdges = [];

  links.forEach((link) => {
    const s = link.sourceNetwork;
    const t = link.targetNetwork;
    if (!s || !t || s === t) return;

    const key = `${s}|${t}`;
    if (edgeSet.has(key)) return;
    edgeSet.add(key);

    synEdges.push({
      data: {
        id: `syn_${s}=>${t}`,
        source: `syn_${s}`,
        target: `syn_${t}`,
      },
    });
  });

  const layoutCy = cytoscape({
    headless: true,
    elements: { nodes: synNodes, edges: synEdges },
  });

  const roots = layoutCy.nodes().filter((n) => n.incomers('edge').length === 0);

  const layoutOpts = {
    name: 'breadthfirst',
    directed: true,
    spacingFactor,
    padding,
    animate: false,
    fit: false,
  };
  if (roots.length) layoutOpts.roots = roots;

  layoutCy.layout(layoutOpts).run();

  const positionsByNetworkId = {};
  layoutCy.nodes().forEach((n) => {
    positionsByNetworkId[n.data('networkId')] = { ...n.position() };
  });

  layoutCy.destroy();
  return { positionsByNetworkId, synNodeCount: synNodes.length, synEdgeCount: synEdges.length };
};

const translateCompoundTo = (cy, parentId, newPos) => {
  if (!cy || !cy.renderer || !cy.renderer()) return false;
  const parent = cy.getElementById(parentId);
  if (!parent || parent.empty()) return false;

  const oldPos = parent.position();
  const dx = newPos.x - oldPos.x;
  const dy = newPos.y - oldPos.y;

  const desc = parent.descendants();
  if (desc && desc.length) {
    desc.positions((n) => ({
      x: n.position('x') + dx,
      y: n.position('y') + dy,
    }));
  }

  parent.position({ x: newPos.x, y: newPos.y });
  return { dx, dy };
};

/**
 * Map tiny synthetic coords -> pixel coords based on real compound sizes.
 *
 * We:
 *  - normalize syn coords to start at (0,0)
 *  - scale by step sizes derived from compound node sizes
 *  - add padding offset so they don't start at exact origin
 */
const scaleSyntheticPositionsToPixels = (positionsByNetworkId, networkNodes, opts = {}) => {
  const padding = opts.padding ?? 80;

  const vals = Object.values(positionsByNetworkId || {});
  if (!vals.length) return {};

  let minX = Infinity; let minY = Infinity;
  let maxX = -Infinity; let maxY = -Infinity;

  vals.forEach((p) => {
    if (typeof p.x === 'number') { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); }
    if (typeof p.y === 'number') { minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
  });

  // Derive spacing steps from real compound node sizes (after render)
  let sumW = 0; let sumH = 0; let maxW = 0; let maxH = 0;
  const count = networkNodes.length || 1;

  networkNodes.forEach((n) => {
    const w = n.data('width');
    const h = n.data('height');
    if (typeof w === 'number') { sumW += w; maxW = Math.max(maxW, w); }
    if (typeof h === 'number') { sumH += h; maxH = Math.max(maxH, h); }
  });

  const avgW = sumW / count;
  const avgH = sumH / count;

  // Conservative minimums so small nodes don't cluster
  const baseStepX = Math.max(maxW, avgW, 320);
  const baseStepY = Math.max(maxH, avgH, 240);

  // If syn graph is tall but narrow, keep x step bigger so columns separate
  const stepX = baseStepX * 1.35;
  const stepY = baseStepY * 1.15;

  const scaled = {};
  Object.keys(positionsByNetworkId).forEach((networkId) => {
    const p = positionsByNetworkId[networkId];
    if (!p) return;

    const nx = (p.x - minX); // normalized to 0
    const ny = (p.y - minY);

    scaled[networkId] = {
      x: padding + nx * stepX,
      y: padding + ny * stepY,
    };
  });

  return scaled;
};

const runInitialModelViewLayout_3B = (cy, model, opts = {}) => {
  const { positionsByNetworkId, synNodeCount, synEdgeCount } = computeNetworkPositionsBreadthfirst(model, opts);
  const networks = cy.nodes('.network-node');

  const scaledPositions = scaleSyntheticPositionsToPixels(
    positionsByNetworkId,
    networks,
    { padding: opts.pixelPadding ?? 100 },
  );

  networks.forEach((netNode) => {
    if (!cy || !cy.renderer || !cy.renderer()) return;
    const networkId = netNode.data('networkId');
    const target = scaledPositions[networkId];

    if (!target) {
      return;
    }

    translateCompoundTo(cy, netNode.id(), target);
  });

  if (opts.fit && cy.renderer && cy.renderer()) {
    cy.fit(cy.nodes('.network-node'), 30);
  }
};

export default function GraphModelView({ data, graphKey }) {
  const [globalState, dispatch] = Store.useStore();

  const graphRef = React.useRef(null);
  const cyRef = React.useRef(null);
  const globalStateRef = React.useRef(globalState);

  const [rightClickedNode, setRightClickedNode] = React.useState(null);

  // Debounce timer for zoom/pan saving
  const zoomPanTimerRef = React.useRef(null);

  // Double-click handling (custom, because cytoscape-dblclick is not in use)
  const clickTimerRef = React.useRef(null);
  const lastClickRef = React.useRef({ time: 0, targetId: null });

  // prevent re-running 3B more than once per mount
  const didRunInitialLayoutRef = React.useRef(false);

  // track topology changes only (not zoom/pan/layout/style)
  const lastTopologyKeyRef = React.useRef('');

  // track whether mappedGraphs has been initialized (to detect model load/reset)
  const lastMappedGraphsKeyRef = React.useRef(null);

  // track viewport initialization to prevent resetting to defaults
  const didInitViewportRef = React.useRef(false);
  const lastAppliedViewportRef = React.useRef({ zoom: null, pan: null });

  // instance counter for Cytoscape identity tracking
  const cyInstanceCounterRef = React.useRef(0);

  const selectedGraphKey = ModellerReducer.getSelectedGraphKey(globalState);

  React.useEffect(() => {
    globalStateRef.current = globalState;
  }, [globalState]);

  const getCy = () => cyRef.current;

  const positionPortIcons = (graph, targetNetworkId = null) => {
    if (!graph || graph.destroyed?.()) return;
    // Guard against calls before renderer is ready/during teardown
    if (!graph.renderer || !graph.renderer()) return;
    const iconWidth = 15;
    const hangOffset = 2;

    const icons = graph.nodes('.input-icon, .output-icon').filter((icon) => {
      if (!targetNetworkId) return true;
      return icon.data('networkId') === targetNetworkId;
    });

    icons.forEach((icon) => {
      const portId = icon.data('portId');
      const side = icon.data('side');
      const portNode = graph.getElementById(portId);
      if (!portNode || portNode.empty()) return;

      const portPos = portNode.position();
      const portWidth = portNode.data('width');
      if (typeof portWidth !== 'number' || Number.isNaN(portWidth)) return;
      const offsetX = (portWidth / 2) + (iconWidth / 2) + hangOffset;
      const dir = side === 'left' ? -1 : 1;
      icon.position({ x: portPos.x + (offsetX * dir), y: portPos.y });
    });
  };

  const saveZoomLevel = React.useCallback(() => {
    const cy = getCy();
    if (!cy) return;

    const updateBody = {
      // eslint-disable-next-line no-underscore-dangle
      zoom: cy._private.zoom,
      zoomPosition: cy.pan(),
    };

    // This dispatch is user-driven (pan/zoom), not on-mount.
    dispatch(CytoReducer.updateModelGraphics(updateBody, true, 'graphics.graph'));
  }, [dispatch]);

  const enqueueZoomPanSave = React.useCallback(() => {
    if (zoomPanTimerRef.current) clearTimeout(zoomPanTimerRef.current);
    zoomPanTimerRef.current = setTimeout(() => {
      saveZoomLevel();
    }, 500);
  }, [saveZoomLevel]);

  const doNodeDoubleClick = (event) => {
    const cy = event.cy || getCy();
    if (!cy) return;

    const { networkId } = event.target.data();
    dispatch(ModellerReducer.setSelectedGraphKey(GraphUtils.escapeSelector(networkId)));
  };

  const resolveNetworkNodeTarget = (target) => {
    if (!target || !target.isNode || !target.isNode()) return null;
    if (target.hasClass('network-node')) return target;
    const parent = target.parent && target.parent();
    if (parent && parent.length && parent.hasClass('network-node')) return parent;
    return null;
  };

  const handleNetworkNodeTap = (event) => {
    const targetNode = resolveNetworkNodeTarget(event.target);
    if (!targetNode) return;

    // Prevent default toggle behavior on already-selected nodes
    if (targetNode.selected && targetNode.selected()) {
      targetNode.unselectify();
    }

    const now = Date.now();
    const targetId = targetNode.id();
    const last = lastClickRef.current;
    const isDoubleClick = last.targetId === targetId && (now - last.time) < 260;

    if (isDoubleClick) {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      lastClickRef.current = { time: 0, targetId: null };
      targetNode.selectify();
      targetNode.select();
      doNodeDoubleClick({ cy: event.cy || getCy(), target: targetNode });
      return;
    }

    lastClickRef.current = { time: now, targetId };
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      targetNode.selectify();
      targetNode.select();
    }, 230);
  };

  /**
   * Build / rebuild Cytoscape when we enter model view OR data meaningfully changes.
   */
  React.useEffect(() => {
    if (graphKey !== MODEL_VIEW_KEY) return () => {};
    if (selectedGraphKey !== graphKey) return () => {};
    if (!data || !data.elements || !graphRef.current) return () => {};

    // Guard: require nodes exist and at least one network-node
    if (!data.elements.nodes || data.elements.nodes.length === 0) return () => {};
    const hasNetworkNode = data.elements.nodes.some((n) => {
      const classes = n?.classes;
      if (Array.isArray(classes)) return classes.includes('network-node');
      if (typeof classes === 'string') return classes.includes('network-node');
      return false;
    });
    if (!hasNetworkNode) return () => {};
    if (!data.elements.edges) return () => {};

    const model = ModellerReducer.getModel(globalStateRef.current);
    const networkCount = model?.networks?.length ?? 0;

    // Detect model load by checking if mappedGraphs has been reset
    // loadModel() sets mappedGraphs = {}, so if mappedGraphs is empty (no keys),
    // it means a model was just loaded and we need to reset state
    const mappedGraphs = globalStateRef.current?.modeller?.mappedGraphs;
    const mappedGraphsIsEmpty = !mappedGraphs || Object.keys(mappedGraphs).length === 0;
    const hasModelViewGraph = mappedGraphs && mappedGraphs[MODEL_VIEW_KEY];
    const mappedGraphsKey = hasModelViewGraph ? MODEL_VIEW_KEY : null;

    // If mappedGraphs is empty, it means loadModel() just ran - force rebuild
    // Track the previous state to detect the transition
    const wasMappedGraphsEmpty = lastMappedGraphsKeyRef.current === 'EMPTY';
    const isModelLoad = mappedGraphsIsEmpty && !wasMappedGraphsEmpty && lastMappedGraphsKeyRef.current !== null;

    if (isModelLoad) {
      // MappedGraphs just became empty (model load detected)
      lastTopologyKeyRef.current = '';
      didRunInitialLayoutRef.current = false;
      didInitViewportRef.current = false;
      lastAppliedViewportRef.current = { zoom: null, pan: null };
      // Explicitly destroy existing cy to force rebuild with fresh model data
      if (cyRef.current && !cyRef.current.destroyed()) {
        try {
          cyRef.current.destroy();
        } catch (e) {
          // ignore
        }
        cyRef.current = null;
      }
    }

    // Also force rebuild if mappedGraphs is empty (model just loaded, graph not yet generated)
    // This ensures we rebuild even if the above detection didn't trigger
    if (mappedGraphsIsEmpty && cyRef.current && !cyRef.current.destroyed()) {
      lastTopologyKeyRef.current = '';
      didRunInitialLayoutRef.current = false;
      didInitViewportRef.current = false;
      lastAppliedViewportRef.current = { zoom: null, pan: null };
      try {
        cyRef.current.destroy();
      } catch (e) {
        // ignore
      }
      cyRef.current = null;
    }

    // Track mappedGraphs state: 'EMPTY' if empty, MODEL_VIEW_KEY if it has the key, null otherwise
    lastMappedGraphsKeyRef.current = mappedGraphsIsEmpty ? 'EMPTY' : mappedGraphsKey;

    // Check if model has valid network positions from the file
    // When loadModel() is called, the model is replaced with the file's version,
    // so this will reflect what's actually in the file (the "ultimate truth")
    const hasNetworkPositions = model?.networks?.some((network) => {
      const p = network.graphics?.graph?.position;
      if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return false;
      if (networkCount > 1 && p.x === 0 && p.y === 0) return false;
      return true;
    });

    // Apply persisted network positions to elements before use
    const patchedElements = applyPersistedNetworkPositionsToElements(data.elements, model);

    // Debug: log patched element positions
    const patchedNetworkPositions = (patchedElements?.nodes || []).reduce((acc, node) => {
      const classes = node?.classes;
      const isNetworkNode = Array.isArray(classes) ? classes.includes('network-node') : (typeof classes === 'string' && classes.includes('network-node'));
      if (isNetworkNode && node?.position) {
        acc[node?.data?.networkId] = node.position;
      }
      return acc;
    }, {});

    // Build key based only on topology (node/edge ids), NOT zoom/pan/layout/style
    const topologyKey = getTopologyKey(patchedElements);

    // CRITICAL FIX: If topology unchanged and cy exists, apply incremental updates and return early.
    // Returning early prevents setting up cleanup that would destroy cy.
    // BUT: Don't take early return if mappedGraphs is empty (model just loaded) - we need to rebuild
    if (topologyKey === lastTopologyKeyRef.current && topologyKey !== '' && cyRef.current && !cyRef.current.destroyed() && !mappedGraphsIsEmpty) {
      const cy = cyRef.current;

      // Update style
      const style = (data.style || []).concat(GraphStyles.getModelViewStyles());
      try { cy.style(style); } catch (e) { /* ignore */ }

      // Apply zoom/pan only if meaningful and not yet initialized
      // CRITICAL: Once viewport is initialized (from saved values or 3B layout), NEVER reapply
      // data.zoom/data.zoomPosition, even if they're meaningful. The viewport is already correct.
      const shouldApplyViewport = !didInitViewportRef.current;
      if (shouldApplyViewport) {
        // eslint-disable-next-line no-underscore-dangle
        const currentZoom = cy._private.zoom;
        const currentPan = cy.pan();

        let shouldApplyZoom = false;
        let shouldApplyPan = false;

        // Check if zoom is meaningful (differs from current by > 0.001)
        if (typeof data.zoom === 'number') {
          const zoomDiff = Math.abs(data.zoom - currentZoom);
          if (zoomDiff > 0.001) {
            shouldApplyZoom = true;
          }
        }

        // Check if pan is meaningful (abs(x) + abs(y) > 5, or differs from current)
        if (data.zoomPosition && typeof data.zoomPosition.x === 'number' && typeof data.zoomPosition.y === 'number') {
          const panMagnitude = Math.abs(data.zoomPosition.x) + Math.abs(data.zoomPosition.y);
          const panDiff = Math.abs(data.zoomPosition.x - currentPan.x) + Math.abs(data.zoomPosition.y - currentPan.y);
          if (panMagnitude > 5 || panDiff > 5) {
            shouldApplyPan = true;
          }
        }

        if (shouldApplyZoom || shouldApplyPan) {
          if (shouldApplyZoom) {
            cy.zoom(data.zoom);
            lastAppliedViewportRef.current.zoom = data.zoom;
          }
          if (shouldApplyPan) {
            cy.pan(data.zoomPosition);
            lastAppliedViewportRef.current.pan = { ...data.zoomPosition };
          }

          didInitViewportRef.current = true;
        }
      }

      // IMPORTANT: do not call cy.layout() and do not touch node positions here
      return () => {};
    }

    // Topology changed: need to rebuild
    lastTopologyKeyRef.current = topologyKey;

    // destroy previous
    if (cyRef.current && !cyRef.current.destroyed()) {
      try { cyRef.current.destroy(); } catch (e) { /* ignore */ }
      cyRef.current = null;
      didRunInitialLayoutRef.current = false;
      didInitViewportRef.current = false;
      lastAppliedViewportRef.current = { zoom: null, pan: null };
    }

    const chartData = {
      container: graphRef.current,
      boxSelectionEnabled: true,
      userZoomingEnabled: false,
      layout: { name: 'preset', fit: false, animate: false },
      zoom: data.zoom,
      pan: data.zoomPosition,
      elements: patchedElements,
      style: (data.style || []).concat(GraphStyles.getModelViewStyles()),
    };

    const cy = cytoscape(chartData);
    cyInstanceCounterRef.current += 1;
    // eslint-disable-next-line no-underscore-dangle
    cy._modelViewInstanceId = cyInstanceCounterRef.current;
    cyRef.current = cy;

    cy.ready(() => {
      // CRITICAL FIX: Preset layout may not respect positions for compound nodes.
      // If we have saved positions, explicitly apply them after Cytoscape is ready.
      // Directly set positions instead of using translateCompoundTo, as preset layout
      // may have already applied positions incorrectly (doubled them).
      if (hasNetworkPositions) {
        cy.nodes('.network-node').forEach((netNode) => {
          const networkId = netNode.data('networkId');
          const savedPos = model?.networks?.find((net) => net.id === networkId)?.graphics?.graph?.position;
          if (savedPos && typeof savedPos.x === 'number' && typeof savedPos.y === 'number') {
            const isDefault = savedPos.x === 0 && savedPos.y === 0;
            if (!isDefault) {
              // Force Cytoscape to resolve position, lazy, cached etc; REQUIRED
              netNode.position();
              // Directly set position - Cytoscape will handle children positions relative to parent
              netNode.position({ x: savedPos.x, y: savedPos.y });
            }
          }
        });
      }
      positionPortIcons(cy);

      // make the compound parents draggable
      cy.nodes('.network-node').forEach((n) => n.grabbable(true));

      // Initialize viewport if meaningful values were provided at creation
      if (!didInitViewportRef.current) {
        // eslint-disable-next-line no-underscore-dangle
        const currentZoom = cy._private.zoom;
        const currentPan = cy.pan();

        if (typeof data.zoom === 'number' && Math.abs(data.zoom - currentZoom) > 0.001) {
          didInitViewportRef.current = true;
          lastAppliedViewportRef.current.zoom = data.zoom;
        }
        if (data.zoomPosition && typeof data.zoomPosition.x === 'number' && typeof data.zoomPosition.y === 'number') {
          const panMagnitude = Math.abs(data.zoomPosition.x) + Math.abs(data.zoomPosition.y);
          const panDiff = Math.abs(data.zoomPosition.x - currentPan.x) + Math.abs(data.zoomPosition.y - currentPan.y);
          if (panMagnitude > 5 || panDiff > 5) {
            didInitViewportRef.current = true;
            lastAppliedViewportRef.current.pan = { ...data.zoomPosition };
          }
        }
      }

      // run 3B once if no persisted positions AND network nodes exist
      const networkNodes = cy.nodes('.network-node');
      if (!hasNetworkPositions && !didRunInitialLayoutRef.current && networkNodes.length > 0) {
        const shouldFit = !data.zoom;
        let attemptCount = 0;
        const maxAttempts = 2;

        const attemptLayout = () => {
          // Guard: prevent rescheduling if layout already ran (e.g., from another data update)
          if (didRunInitialLayoutRef.current) {
            return;
          }

          // Verify widths/heights are > 0 before calling 3B
          const allHaveSize = networkNodes.every((n) => {
            const w = n.data('width');
            const h = n.data('height');
            return typeof w === 'number' && w > 0 && typeof h === 'number' && h > 0;
          });

          if (!allHaveSize && attemptCount < maxAttempts) {
            attemptCount += 1;
            requestAnimationFrame(() => requestAnimationFrame(attemptLayout));
            return;
          }

          // IMPORTANT: scale syn coords -> pixels (inside runInitialModelViewLayout_3B)
          runInitialModelViewLayout_3B(cy, model, {
            fit: shouldFit,
            spacingFactor: 0.5,
            padding: 10,
            // pixelPadding: 120,
          });
          positionPortIcons(cy);

          didRunInitialLayoutRef.current = true;
          didInitViewportRef.current = true;

          // Persist initial network positions AFTER the click stack unwinds.
          // (This avoids the ResizeGroup max-depth issues you hit earlier.)
          setTimeout(() => {
            // Guard: check again in case cy was destroyed or layout was reset
            if (didRunInitialLayoutRef.current && cyRef.current === cy && !cy.destroyed()) {
              const currentState = globalStateRef.current;
              const currentSelectedGraphKey = ModellerReducer.getSelectedGraphKey(currentState);
              if (currentSelectedGraphKey !== MODEL_VIEW_KEY) return;

              const batch = [];
              cy.nodes('.network-node').forEach((node) => {
                const { networkId } = node.data();
                const position = node.position();
                const size = { width: node.width(), height: node.height() };
                batch.push({
                  networkId,
                  updateBody: {
                    position,
                    size,
                  },
                });
              });

              if (batch.length) {
                dispatch(CytoReducer.updateNetworkGraphics({ updateBatch: batch, updatePath: 'graphics.graph' }));
              }
            }
          }, 250);
        };

        requestAnimationFrame(() => requestAnimationFrame(attemptLayout));
      }
    });

    // Events
    cy.on('cxttapstart', 'node.primary', (event) => setRightClickedNode(event.target));
    cy.on('panend', () => enqueueZoomPanSave());


    cy.on('tap', 'node', handleNetworkNodeTap);

    // Persist network drag (user-driven -> safe to dispatch)
    cy.on('dragfreeon', 'node.network-node', (event) => {
      const { networkId } = event.target.data();
      const position = event.target.position();
      const size = { width: event.target.width(), height: event.target.height() };

      dispatch(CytoReducer.updateNetworkGraphics({
        updateBatch: [{ networkId, updateBody: { position, size } }],
        updatePath: 'graphics.graph',
      }));
      positionPortIcons(event.cy, networkId);
    });

    cy.on('drag', 'node.network-node', (event) => {
      const { networkId } = event.target.data();
      positionPortIcons(event.cy, networkId);
    });

    // CRITICAL: Capture topologyKey in cleanup closure to check if topology changed
    const topologyKeyForCleanup = topologyKey;
    return () => {
      // Only destroy cy if topology actually changed
      // Compare the topology key from when this effect ran with the current topology key
      const topologyStillMatches = topologyKeyForCleanup === lastTopologyKeyRef.current;

      if (!topologyStillMatches) {
        // Topology changed, safe to destroy
        try { cy.destroy(); } catch (e) { /* ignore */ }
        if (cyRef.current === cy) cyRef.current = null;
      }
      // If topology unchanged, preserve cy instance (don't destroy, don't clear cyRef.current)
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
    };
  }, [data, selectedGraphKey]);

  /**
   * Context menus: recreate when rightClickedNode / permissions change
   */
  React.useEffect(() => {
    const cy = getCy();
    if (!cy) return () => {};

    const modelViewMode = globalState.config?.generalConfig?.modelViewMode;
    const modelViewCustomPermissions = modelViewMode === 'custom'
      ? (globalState.config?.generalConfig?.modelViewCustomPermissions ?? {})
      : {};

    return () => {};
  }, [
    globalState.config?.generalConfig?.modelViewMode,
    globalState.config?.generalConfig?.modelViewCustomPermissions,
    globalState.modeller,
    rightClickedNode,
  ]);

  // Wheel zoom
  const wheelListener = React.useCallback((event) => {
    if (!event.ctrlKey) return;

    const cy = getCy();
    if (!cy) return;

    // eslint-disable-next-line no-underscore-dangle
    const currZoom = cy._private.zoom;
    const zoomMod = (event.deltaY > 0) ? -1 : 1;
    const newZoomLevel = Math.max(0.1, currZoom + zoomMod * 0.05);

    cy.zoom({
      level: newZoomLevel,
      renderedPosition: { x: event.offsetX, y: event.offsetY },
    });

    enqueueZoomPanSave();
    event.preventDefault();
    event.stopPropagation();
  }, [enqueueZoomPanSave]);

  React.useEffect(() => {
    const el = graphRef.current;
    if (!el) return () => {};
    el.addEventListener('wheel', wheelListener, { passive: false });
    return () => el.removeEventListener('wheel', wheelListener);
  }, [wheelListener]);

  // Keyboard shortcuts
  const keyboardListener = React.useCallback((event) => {
    const cy = getCy();
    if (!cy) return;

    if (event.ctrlKey && (event.keyCode === 48 || event.key === '0')) {
      cy.reset();
      enqueueZoomPanSave();
    }

    if (event.altKey && (event.keyCode === 49 || event.key === '1')) {
      cy.fit(cy.nodes(), 10);
      enqueueZoomPanSave();
    }

    if (event.ctrlKey && (event.keyCode === 97 || event.keyCode === 65 || event.key === 'a' || event.key === 'A')) {
      cy.nodes().select();
      event.preventDefault();
      event.stopPropagation();
    }

    if (event.ctrlKey && (event.keyCode === 189 || event.keyCode === 109 || event.key === '-' || event.code === 'Minus' || event.code === 'NumpadSubtract')) {
      // eslint-disable-next-line no-underscore-dangle
      const currZoom = cy._private.zoom;
      const newZoom = Math.max(0.1, currZoom - 0.05);
      cy.zoom({ level: newZoom });
      enqueueZoomPanSave();
      event.preventDefault();
      event.stopPropagation();
    }

    if (event.ctrlKey && (event.keyCode === 187 || event.keyCode === 107 || event.key === '+' || event.code === 'Equal' || event.code === 'NumpadAdd')) {
      // eslint-disable-next-line no-underscore-dangle
      const currZoom = cy._private.zoom;
      const newZoom = Math.max(0.1, currZoom + 0.05);
      cy.zoom({ level: newZoom });
      enqueueZoomPanSave();
      event.preventDefault();
      event.stopPropagation();
    }
  }, [enqueueZoomPanSave]);

  React.useEffect(() => {
    document.addEventListener('keydown', keyboardListener);
    return () => {
      document.removeEventListener('keydown', keyboardListener);
      saveZoomLevel();
    };
  }, [keyboardListener, saveZoomLevel]);

  return (
    <div
      id={`graph_container_${graphKey}`}
      className={componentStyles.container}
      ref={graphRef}
    />
  );
}
